import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Typography, Alert, CircularProgress } from '@mui/material';

import cameraIcon from '../../../../../../assets/icons/camera.png';
import { setOnboardingComplete } from '../../../../../../redux/slices/currentUserSlice';
import { updateExistingUser, uploadImageToStorage } from '../../../../../../redux/actions/usersActions';
import iosPhotosIcon from '../../../../../../assets/images/Photos.png';

import { TakePhoto } from './components'

import {
  PhotoStepContainer,
  UploadOptionsContainer,
  UploadOptionButton,
  SkipLink,
  ProfilePhoto,
  SavePhotoButton,
  PhotoAndDescriptionContainer,
} from './ConfigurePhotoStep.styles';

const ConfigurePhotoStep = () => {
  const currentUser = useSelector((state) => state.currentUser);
  const employeeInfo = useMemo(() => currentUser?.employeeInfo || {}, [currentUser]);
  const [photo, setPhoto] = useState(employeeInfo?.profileImageUrl);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();


  // Sincronizar `photo` con `employeeInfo` si este cambia
  useEffect(() => {
    setPhoto(employeeInfo?.profileImageUrl);
  }, [employeeInfo]);

  // Limpiar URLs locales si es necesario
  useEffect(() => {
    return () => {
      if (photo && photo.startsWith("blob:")) {
        URL.revokeObjectURL(photo);
      }
    };
  }, [photo]);

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (!file || !file.type.startsWith("image/")) {
      setError("Please upload a valid image file.");
      return;
    }
    setIsUpdating(true)
    const imageUrl = URL.createObjectURL(file);
    setPhoto(imageUrl);
    setFile(file);

    // Verificación
    if (!imageUrl || !file) {
      setError("Failed to process the photo. Please try again.");
    } else {
      setError("");
    }
  };

  const handleCapture = (screenshot) => {
    setIsUpdating(true)
    setPhoto(screenshot); // Guarda la foto capturada
    const blob = dataURLToBlob(screenshot);
    const fileFromBlob = new File([blob], "profilePhoto.jpg", { type: "image/jpeg" });
    setFile(fileFromBlob);
    setShowWebcam(false);
  };

  const dataURLToBlob = (dataURL) => {
    const byteString = atob(dataURL.split(",")[1]);
    const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];
    const buffer = new Uint8Array(byteString.length);

    for (let i = 0; i < byteString.length; i++) {
      buffer[i] = byteString.charCodeAt(i);
    }

    return new Blob([buffer], { type: mimeString });
  };

  const handleSavePhoto = async () => {
    if (!file) return;
    setLoading(true);

    try {
      const employeeId = employeeInfo?.id;

      if (!employeeId) {
        throw new Error("User ID not found.");
      }

      // We upload the image and retrieve its final URL
      const downloadURL = await uploadImageToStorage(file, employeeId);

      // Ensure we save the new profile-image into the employee's data
      dispatch(updateExistingUser(employeeId, { profileImageUrl: downloadURL }));

      navigate('/loading');
    } catch (error) {
      console.error(error);
      setError("Failed to upload the photo. Please try again.");
    } finally {
      setLoading(false);
      setIsUpdating(false)
    }
  };


  const handleSkip = () => {
    if (currentUser?.isAuthenticated && currentUser?.isUnionVerified) {
      dispatch(setOnboardingComplete());
      navigate('/loading');
    } else if (!currentUser?.isAuthenticated) {
      navigate('/login');
    } else if (!currentUser?.isUnionVerified) {
      navigate('/login/onboarding');
    }
  };

  return (
    <PhotoStepContainer>
      {/* Muestra el avatar y descripción solo si la cámara no está activa */}
      {!showWebcam && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <PhotoAndDescriptionContainer>
            <ProfilePhoto src={photo} alt="Profile Photo" />
            <Typography variant="bodyRegular" align="center" padding="8px 16px" color="#EBEBF599">
              This photo will be used to represent you and be seen by everyone at the restaurant.
            </Typography>
          </PhotoAndDescriptionContainer>
        </motion.div>
      )}

      {error && (
        <Alert severity="error" sx={{ marginBottom: '16px' }}>
          {error}
        </Alert>
      )}

      {!showWebcam ? (
        <UploadOptionsContainer>
          <UploadOptionButton component="label" isFirst="isFirst">
            <Typography variant="bodyRegular" align="center">Choose from Photos</Typography>
            <img src={iosPhotosIcon} alt="Choose from Photos" style={{ size: '28px' }} />
            <input type="file" accept="image/*" hidden onChange={handlePhotoUpload} aria-label="Upload a photo" />
          </UploadOptionButton>
          <UploadOptionButton onClick={() => setShowWebcam(true)}>
            <Typography variant="bodyRegular" align="center">New photo</Typography>
            <img src={cameraIcon} alt="Camera Icon" style={{ width: '28px', height: '28px' }} />
          </UploadOptionButton>
        </UploadOptionsContainer>
      ) : (
        <TakePhoto onCapture={handleCapture} onCancel={() => setShowWebcam(false)} />
      )}

      {!showWebcam && (
        <SavePhotoButton
          variant="contained"
          color="primary"
          onClick={handleSavePhoto}
          disabled={loading || !isUpdating} // Habilita el botón si hay una imagen seleccionada
          aria-busy={loading}
          aria-disabled={loading}
        >
          {loading && <CircularProgress size={20} color="inherit" />}
          {loading ? 'Saving...' : 'Looks good'}
        </SavePhotoButton>
      )}
      {!showWebcam && (
        <SkipLink variant="bodyRegular" onClick={handleSkip}>
          Skip, not recommended
        </SkipLink>
      )}

    </PhotoStepContainer>
  );
};

export default ConfigurePhotoStep;
